import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronLeft, FileSpreadsheet, Loader2, Plus, Sparkles, Trash2, Upload, Download,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { scanPreviousCertificate, getScanPreviousJob, exportScanPreviousCsv } from '../../services/api';
import { preprocessFilesForUpload } from '../../lib/pdfRasterise';

// Bumped each time we materially change the scan flow. Rendered in
// the page header so admins (and the agent) can verify the latest
// JS bundle is actually being served from the browser. Cached chunks
// are the most common reason "the fix didn't work" — bumping this
// string forces a new build hash, and seeing it in the UI proves the
// new bundle is live.
const SCAN_FLOW_VERSION = '2026-05-13-client-rasterise';

/**
 * Scan a previous tester's paper EICR.
 *
 * Workflow:
 *   1. Upload one or more page photos.
 *   2. AI OCRs each page → flat list of circuit rows.
 *   3. Engineer reviews + edits in an editable table.
 *   4. Approve → CSV download with the exact 33-column header row
 *      from the office spreadsheet.
 *
 * The table is wide; it scrolls horizontally inside its own
 * container so the surrounding chrome (sidebar / header / footer)
 * stays visible.
 */

const COLUMNS = [
  ['circuit_no',             'Circuit'],
  ['description',            'Description'],
  ['type_of_wiring',         'Wiring'],
  ['reference_method',       'Ref'],
  ['points_served',          'Pts'],
  ['csa_live',               'Csa Live'],
  ['csa_cpc',                'Csa cpc'],
  ['max_disconnection_time', 'Disc t'],
  ['opd_bs_en',              'OPD BS(EN)'],
  ['opd_type',               'Type'],
  ['opd_rating',             'A'],
  ['opd_breaking_capacity',  'kA'],
  ['max_zs',                 'Max Zs'],
  ['rcd_bs_en',              'RCD BS(EN)'],
  ['rcd_type',               'RCD Type'],
  ['rcd_operating_current',  'IΔn mA'],
  ['rcd_rating',             'RCD A'],
  ['r1',                     'r1'],
  ['rn',                     'rn'],
  ['r2',                     'r2'],
  ['r1_r2',                  'R1+R2'],
  ['r2_only',                'R2'],
  ['test_voltage',           'V'],
  ['live_live',              'L–L'],
  ['live_neutral',           'L–N'],
  ['live_earth',             'L–E'],
  ['neutral_earth',          'N–E'],
  ['polarity',               'Pol'],
  ['zs_measured',            'Zs meas'],
  ['rcd_disco_time_1x',      'Δt 1×'],
  ['rcd_disco_time_5x',      'Δt 5×'],
  ['rcd_test_button',        'RCD btn'],
  ['afdd_test_button',       'AFDD btn'],
];

const NEW_ROW = () => COLUMNS.reduce((acc, [k]) => ({ ...acc, [k]: '' }), {});


const CertScan = () => {
  const auth = useAuth();
  const fileRef = useRef(null);
  const [pages, setPages] = useState([]); // [{file, previewUrl}]
  const [scanning, setScanning] = useState(false);
  // Live progress from the polling loop — populated each tick so the
  // admin sees "Rasterising…" → "OCR 3 of 6…" → done. Keeps long
  // multi-page scans from feeling like a frozen spinner.
  const [progress, setProgress] = useState(null);
  const [rows, setRows] = useState([]);
  const [filename, setFilename] = useState('');
  const [exporting, setExporting] = useState(false);

  const base = auth?.user?.role === 'admin' ? '/admin' : '/employee';

  const onPickPages = (e) => {
    const fl = Array.from(e.target.files || []);
    if (!fl.length) return;
    setPages(prev => [
      ...prev,
      ...fl.map(f => ({
        file: f,
        // Browsers can't render PDF blobs in <img>, so we keep the
        // preview URL only for image uploads. PDFs fall back to a
        // generic icon tile in the picker grid.
        previewUrl: f.type?.startsWith('image/') ? URL.createObjectURL(f) : null,
        isPdf: f.type === 'application/pdf' || /\.pdf$/i.test(f.name || ''),
      })),
    ]);
    e.target.value = '';
  };

  const removePage = (idx) => {
    setPages(prev => {
      const next = [...prev];
      const removed = next.splice(idx, 1)[0];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return next;
    });
  };

  const runScan = async () => {
    if (!pages.length) {
      toast.error('Add at least one page photo or PDF');
      return;
    }
    setScanning(true);
    setProgress({ stage: 'preprocessing', pagesCompleted: 0, pageCount: 0 });
    setRows([]);
    try {
      // Client-side PDF → JPEGs to dodge the Cloudflare 524 we hit
      // on production with multi-page PDFs. preprocessFilesForUpload
      // returns image Files for any PDFs (1 per page) and passes
      // through everything else untouched. Falls back to raw PDF
      // upload if pdfjs chokes.
      const filesToSend = await preprocessFilesForUpload(pages.map(p => p.file));
      setProgress({ stage: 'uploading', pagesCompleted: 0, pageCount: filesToSend.length });
      const start = await scanPreviousCertificate(filesToSend);
      const jobId = start?.jobId;
      const effectivePages = start?.pageCount || filesToSend.length;
      if (!jobId) throw new Error('No job ID returned');
      setProgress({ stage: 'rasterising', pagesCompleted: 0, pageCount: effectivePages });
      // Poll the job — backend rasterises PDFs + runs OCR in
      // parallel, but multi-page totals can exceed the 60 s gateway
      // budget if we wait synchronously. 2 s cadence × ~60 attempts
      // gives a generous 2 min ceiling.
      let job;
      for (let i = 0; i < 90; i++) {
        await new Promise(r => setTimeout(r, 2000));
        job = await getScanPreviousJob(jobId);
        // Push the latest stage/counts into state so the inline
        // "Rasterising page X of N" / "OCR Y of N" text re-renders
        // every poll tick.
        setProgress({
          stage: job?.stage || job?.status || 'scanning',
          pagesCompleted: job?.pagesCompleted || 0,
          pageCount: job?.pageCount || effectivePages,
        });
        if (job.status === 'done' || job.status === 'error') break;
      }
      if (!job || (job.status !== 'done' && job.status !== 'error')) {
        throw new Error('Scan timed out — try again with fewer pages');
      }
      if (job.status === 'error') {
        throw new Error(job.error || 'Scan failed');
      }
      const out = Array.isArray(job?.rows) ? job.rows : [];
      setRows(out);
      if (out.length === 0) {
        toast.warning('AI returned no rows — try sharper photos or check the PDF page is the test-results table');
      } else if (job.error) {
        toast.warning(`Extracted ${out.length} rows but ${job.error}`);
      } else {
        toast.success(`Extracted ${out.length} circuit row${out.length === 1 ? '' : 's'}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Scan failed');
    } finally {
      setScanning(false);
      setProgress(null);
    }
  };

  const updCell = (rowIdx, key, val) => {
    setRows(prev => prev.map((r, i) => (i === rowIdx ? { ...r, [key]: val } : r)));
  };
  const addRow = () => setRows(prev => [...prev, NEW_ROW()]);
  const deleteRow = (idx) => setRows(prev => prev.filter((_, i) => i !== idx));

  const exportCsv = async () => {
    if (!rows.length) {
      toast.error('Nothing to export — add or scan rows first');
      return;
    }
    setExporting(true);
    try {
      await exportScanPreviousCsv(rows, filename || 'lutrom-scan-previous');
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error(e?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const totalPts = useMemo(() => rows.length, [rows.length]);

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-[1700px] mx-auto" data-testid="cert-scan-page">
      <Link to={`${base}/certificates`} className="inline-flex items-center text-xs text-slate-500 hover:text-slate-800">
        <ChevronLeft className="h-3 w-3 mr-1" /> Back to certificates
      </Link>

      <div className="flex items-start gap-3">
        <FileSpreadsheet className="h-6 w-6 text-emerald-700 mt-0.5" />
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2 flex-wrap">
            Scan previous certificate
            {/* Build-version pill — proves which JS bundle is live. If
                you don't see this pill, your browser is serving cached
                JS; hard-refresh or clear site data. */}
            <span
              className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-[10px] font-mono px-2 py-0.5 tracking-tight"
              title="JS bundle version — bumped on material changes to the scan flow"
              data-testid="scan-flow-version"
            >
              build {SCAN_FLOW_VERSION}
            </span>
          </h1>
          <p className="text-sm text-slate-600 max-w-3xl mt-1">
            Photograph the previous tester's paper EICR pages, let the AI OCR every circuit row, review and edit on screen,
            then download a CSV in the office's exact 33-column layout — drops straight into the spreadsheet workflow.
          </p>
        </div>
      </div>

      {/* Page upload */}
      <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Pages</h2>
            <div className="text-[11px] text-slate-500">Upload one image per A4 page. Multi-page certs scan top-to-bottom.</div>
          </div>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={onPickPages} />
            <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="cert-scan-pick-pages">
              <Upload className="h-4 w-4 mr-2" /> Add page{pages.length ? 's' : ''} <span className="hidden sm:inline text-slate-400 font-normal text-[10px] ml-1">(image / PDF)</span>
            </Button>
            <Button
              onClick={runScan}
              disabled={!pages.length || scanning}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="cert-scan-run-ai"
            >
              {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {scanning ? 'Scanning…' : 'Scan with AI'}
            </Button>
          </div>
        </div>
        {/* Live progress — only shown while a scan is in flight. The
            backend reports a stage (rasterising / ocr / done) and a
            pagesCompleted counter; we map both into one human line so
            the engineer never wonders whether anything's happening on
            a long multi-page PDF. */}
        {scanning && progress && (
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 flex items-center gap-3 text-xs text-emerald-900"
            data-testid="cert-scan-progress"
            role="status"
            aria-live="polite"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium">
                {progress.stage === 'preprocessing' && 'Rasterising PDF pages on your device…'}
                {progress.stage === 'uploading' && 'Uploading pages…'}
                {progress.stage === 'pending' && 'Queued…'}
                {progress.stage === 'rasterising' && 'Rasterising PDF pages…'}
                {progress.stage === 'ocr' && progress.pageCount > 0 && (
                  <>OCR {Math.min(progress.pagesCompleted, progress.pageCount)} of {progress.pageCount} page{progress.pageCount === 1 ? '' : 's'}…</>
                )}
                {progress.stage === 'ocr' && !progress.pageCount && 'Reading circuit rows…'}
                {progress.stage === 'done' && 'Finishing up…'}
              </div>
              {progress.stage === 'ocr' && progress.pageCount > 0 && (
                <div className="mt-1 h-1 bg-emerald-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-600 transition-[width] duration-500 ease-out"
                    style={{ width: `${Math.min(100, (progress.pagesCompleted / progress.pageCount) * 100)}%` }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {pages.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {pages.map((p, i) => (
              <div key={i} className="relative group rounded border border-slate-200 overflow-hidden bg-slate-50">
                {p.previewUrl ? (
                  <img src={p.previewUrl} alt={`page ${i + 1}`} className="w-full h-32 object-cover" />
                ) : (
                  <div className="w-full h-32 flex flex-col items-center justify-center text-slate-500 bg-rose-50/40">
                    <FileSpreadsheet className="h-8 w-8 text-rose-600 mb-1" />
                    <div className="text-[10px] font-mono uppercase tracking-wide text-rose-700">PDF</div>
                    <div className="text-[10px] truncate max-w-[90%] text-center text-slate-500">{p.file?.name || 'document.pdf'}</div>
                  </div>
                )}
                <div className="absolute top-1 left-1 bg-slate-900/80 text-white text-[10px] font-mono px-1.5 py-0.5 rounded">p{i + 1}</div>
                <button
                  type="button"
                  onClick={() => removePage(i)}
                  className="absolute top-1 right-1 bg-white/90 hover:bg-red-50 hover:text-red-600 text-slate-600 rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`cert-scan-remove-page-${i}`}
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-400 italic py-6 text-center border-2 border-dashed border-slate-200 rounded">
            No pages yet — click <span className="font-medium">Add page</span> to start.
          </div>
        )}
      </section>

      {/* Review + edit table */}
      {rows.length > 0 && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="cert-scan-review">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <h2 className="text-sm font-semibold text-slate-800">Review extracted rows</h2>
              <div className="text-[11px] text-slate-500">{totalPts} row{totalPts === 1 ? '' : 's'} — edit any cell, delete bad reads, then export.</div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1">
                <Label className="text-xs text-slate-500">Filename</Label>
                <Input
                  value={filename}
                  onChange={e => setFilename(e.target.value)}
                  placeholder="LFC-S1-SCERRIES-MAY-2025"
                  className="h-8 w-56"
                  data-testid="cert-scan-filename"
                />
              </div>
              <Button variant="outline" size="sm" onClick={addRow} data-testid="cert-scan-add-row">
                <Plus className="h-3 w-3 mr-1" /> Row
              </Button>
              <Button
                onClick={exportCsv}
                disabled={exporting}
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="cert-scan-export-csv"
              >
                {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                Approve &amp; download CSV
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto -mx-4 sm:-mx-0">
            <table className="text-[11px] font-mono border-collapse w-max">
              <thead>
                <tr className="bg-slate-50 text-slate-700">
                  <th className="sticky left-0 bg-slate-50 px-2 py-1.5 text-left z-10 border-b border-slate-200">#</th>
                  {COLUMNS.map(([k, label]) => (
                    <th key={k} className="px-1.5 py-1.5 text-left whitespace-nowrap border-b border-slate-200 font-semibold uppercase tracking-wide text-[10px]">
                      {label}
                    </th>
                  ))}
                  <th className="sticky right-0 bg-slate-50 px-2 py-1.5 z-10 border-b border-slate-200" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri} className="hover:bg-emerald-50/30 border-b border-slate-100">
                    <td className="sticky left-0 bg-white hover:bg-emerald-50/30 px-2 py-0.5 text-slate-400 z-10 border-r border-slate-100">{ri + 1}</td>
                    {COLUMNS.map(([k]) => (
                      <td key={k} className="px-0.5 py-0.5">
                        <input
                          value={row[k] || ''}
                          onChange={e => updCell(ri, k, e.target.value)}
                          className="w-[72px] px-1.5 py-1 border border-transparent hover:border-slate-200 focus:border-emerald-400 focus:bg-white rounded text-[11px] outline-none transition-colors"
                          data-testid={`cert-scan-cell-${ri}-${k}`}
                        />
                      </td>
                    ))}
                    <td className="sticky right-0 bg-white hover:bg-emerald-50/30 px-1 z-10 border-l border-slate-100">
                      <button
                        type="button"
                        onClick={() => deleteRow(ri)}
                        className="text-slate-300 hover:text-red-600"
                        title="Delete row"
                        data-testid={`cert-scan-delete-row-${ri}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};

export default CertScan;
